import { useEffect, useMemo, useState } from "react";
import { Chart as GoogleChart } from "react-google-charts";
import { toast } from "sonner";
import { X, Loader2, Activity, TrendingUp, TrendingDown, Package } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * StockMovementsModal — full audit trail + trend chart for a single product.
 *
 * Opens when the admin clicks the "Movimentos" (📈) icon on a product row.
 * Shows:
 *  - 3 KPIs (stock atual, total entradas, total saídas)
 *  - line chart of balance over time (google-charts, same library as finance)
 *  - table of every movement with kind badge, delta, balance, reference
 */
function eur(n) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const KIND_META = {
  invoice:          { label: "Entrada FTR",    color: "#10b981", bg: "#10b98122" },
  invoice_reversal: { label: "Reversão FTR",   color: "#ef4444", bg: "#ef444422" },
  manual:           { label: "Ajuste manual",  color: "#d4af37", bg: "#d4af3722" },
  consumption:      { label: "Consumo",        color: "#f97316", bg: "#f9731622" },
};

export default function StockMovementsModal({ productId, productName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/stock/products/${productId}/movements`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [productId]);

  const chartData = useMemo(() => {
    if (!data?.trend?.length) return null;
    const header = ["Data", "Stock"];
    const rows = data.trend.map(t => [new Date(t.date), Number(t.balance)]);
    return [header, ...rows];
  }, [data]);

  const chartOptions = useMemo(() => ({
    backgroundColor: "transparent",
    legend: "none",
    curveType: "function",
    lineWidth: 3,
    pointSize: 5,
    colors: ["#d4af37"],
    vAxis: {
      textStyle: { color: "#a0b2af", fontSize: 11 },
      gridlines: { color: "#1a464066" },
      baselineColor: "#1a4640",
      title: "Stock",
      titleTextStyle: { color: "#d4af37", italic: false, fontSize: 11 },
    },
    hAxis: {
      textStyle: { color: "#a0b2af", fontSize: 10 },
      gridlines: { color: "transparent" },
      format: "d MMM",
    },
    chartArea: { left: 50, right: 20, top: 20, bottom: 40, width: "90%", height: "80%" },
    tooltip: { textStyle: { color: "#031c18", fontSize: 12 } },
  }), []);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="stock-movements-modal">
      <div className="lux-card w-full max-w-4xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Activity size={12} /> Histórico de movimentos de stock
            </div>
            <div className="font-display text-xl">{productName}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="stock-movements-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-6">
          {loading || !data ? (
            <div className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-[#d4af37]" /></div>
          ) : (
            <>
              {/* KPIs */}
              <section className="grid grid-cols-3 gap-3">
                <Kpi label="Stock atual"
                     value={data.current_stock}
                     icon={<Package size={14} />}
                     color={data.current_stock > 0 ? "#10b981" : "#a0b2af"}
                     testId="stock-mov-kpi-current" />
                <Kpi label="Total entradas"
                     value={`+ ${data.totals.entries}`}
                     icon={<TrendingUp size={14} />}
                     color="#10b981"
                     testId="stock-mov-kpi-entries" />
                <Kpi label="Total saídas"
                     value={`− ${data.totals.exits}`}
                     icon={<TrendingDown size={14} />}
                     color="#ef4444"
                     testId="stock-mov-kpi-exits" />
              </section>

              {/* Trend chart */}
              {chartData && chartData.length > 2 ? (
                <section className="lux-card p-4">
                  <div className="eyebrow text-[10px] text-[#a0b2af] mb-2">
                    Tendência do stock — cada ponto é um movimento
                  </div>
                  <div style={{ height: 260 }} data-testid="stock-mov-chart">
                    <GoogleChart
                      chartType="AreaChart"
                      data={chartData}
                      options={chartOptions}
                      width="100%"
                      height="100%"
                    />
                  </div>
                </section>
              ) : (
                <section className="lux-card p-6 text-center text-sm text-[#a0b2af]">
                  Ainda não há histórico suficiente para desenhar uma tendência.
                  Crie uma fatura ou registe consumo para começar a ver o gráfico.
                </section>
              )}

              {/* Movements table */}
              <section>
                <div className="eyebrow text-[10px] text-[#a0b2af] mb-2">
                  {data.totals.count} movimento(s) registado(s) · mais recentes primeiro
                </div>
                {data.movements.length === 0 ? (
                  <div className="lux-card p-6 text-center text-sm text-[#a0b2af]"
                       data-testid="stock-mov-empty">
                    Nenhum movimento registado ainda.
                  </div>
                ) : (
                  <div className="lux-card overflow-hidden">
                    <table className="w-full text-sm" data-testid="stock-mov-table">
                      <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af] border-b border-[#1a4640]">
                        <tr>
                          <th className="text-left py-2 px-3">Data</th>
                          <th className="text-left py-2 px-3">Tipo</th>
                          <th className="text-right py-2 px-3">Variação</th>
                          <th className="text-right py-2 px-3">Saldo</th>
                          <th className="text-left py-2 px-3">Referência</th>
                          <th className="text-left py-2 px-3">Nota</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#1a4640]/40">
                        {data.movements.map(m => {
                          const meta = KIND_META[m.kind] || { label: m.kind, color: "#a0b2af", bg: "#a0b2af22" };
                          const positive = m.delta > 0;
                          return (
                            <tr key={m.id} data-testid={`stock-mov-row-${m.id}`}>
                              <td className="py-2 px-3 text-xs text-[#a0b2af]">{fmtDateTime(m.created_at)}</td>
                              <td className="py-2 px-3">
                                <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-sm"
                                      style={{ color: meta.color, backgroundColor: meta.bg }}>
                                  {meta.label}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums font-display"
                                  style={{ color: positive ? "#10b981" : "#ef4444" }}>
                                {positive ? "+" : ""}{m.delta}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums text-[#fdfbf7]">
                                {m.balance_after}
                              </td>
                              <td className="py-2 px-3 text-xs font-mono text-[#d4af37]">
                                {m.ref_label || "—"}
                              </td>
                              <td className="py-2 px-3 text-xs text-[#a0b2af] italic">
                                {m.note || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, color, testId }) {
  return (
    <div className="lux-card p-4" data-testid={testId}>
      <div className="eyebrow text-[10px] flex items-center gap-1.5 text-[#a0b2af]">
        {icon} {label}
      </div>
      <div className="font-display text-3xl mt-2 tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
